<template>
  <div class="reporting__time-machine">
    <h3 class="title">
      <i class="icon icon--logo-time"></i>
      {{ $t('reporting.timeMachine.title') }}
    </h3>
    <div class="content">
      <div ref="logContainer" class="log-container" :class="{isData: isData}" @mouseover="isHover = true" @mouseleave="onContainerLeave">
        <div class="log-header">
          <div class="log-header__title">
            {{ $t('reporting.timeMachine.log') }}
          </div>
          <div class="log-header__actions">
            <button v-if="!isShared" class="log-header__actions__button" @click="onAddReservation">
              <i class="icon icon--plus-white" />
              <span class="text">
                {{ $t('reporting.timeMachine.reservation') }}
              </span>
            </button>
          </div>
        </div>
        <div v-if="timeMachineData.length" class="log-content__item__label">
          <template v-for="index in 5">
            <span class="log-content__item__label__text" :key="index" :class="[labels[index-1], $i18n.locale]">
              {{ $t(`reporting.timeMachine.labels[${index - 1}]`) }}
            </span>
          </template>
        </div>
        <div ref="logBody" class="log-content" @scroll="onLogBodyScroll">
          <template v-if="!timeMachineData.length">
            <div class="log-content__icon">
              <img v-if="isHover" class="wait-icon" :src="require('@/assets/img/heatmap/icon-time-machine-wait-hover.svg?url')" />
              <i v-else class="icon icon--time-machine-wait" />
            </div>
            <div class="log-content__text" v-html="$t('reporting.timeMachine.wait')" />
          </template>
          <template v-else>
            <div class="log-content__item__body">
              <div v-for="(item, idx) in timeMachineData" :key="item._id || idx"
                   class="log-content__item__row" :class="{ selected: isSelected(item), waiting: getDisplayStatus(item) === 'reservation' }"
                   :style="getRowStyle(item)"
                   @click="onSelectRow(item)">
                <span class="log-content__item__row__cell control" :class="getDisplayStatus(item)"
                      @mouseenter="onTooltipEnter(item, idx, $event)" @mouseleave="clearTooltip">
                  <img v-if="getIconStatus(item) === 'start'" class="start-icon" :src="require('@/assets/img/heatmap/icon-time-machine-start.svg?url')" />
                  <i v-else class="icon" :class="getStatusIcon(item)"></i>
                </span>
                <span class="log-content__item__row__cell date">
                  <span class="date-text">{{ formatTzDate(item.startDate) }}</span>
                  <span class="time-text">{{ formatTzTime(item.startDate) }}</span>
                </span>
                <span class="log-content__item__row__cell date">
                  <span class="date-text">{{ formatTzDate(item.endDate) }}</span>
                  <span class="time-text">{{ formatTzTime(item.endDate) }}</span>
                </span>
                <span class="log-content__item__row__cell duration">{{ item.duration }}m</span>
                <span class="log-content__item__row__cell report" @click="onClickReport(item)">
                  <i class="icon icon--time-machine-report" :class="{disabled: isReportDisabled(item)}"></i>
                </span>
                <button v-if="getDisplayStatus(item) === 'reservation' && !isShared" class="log-content__item__row__edit-btn" @click.stop="onEditReservation(item)">
                  {{ $t('reporting.timeMachine.editReservation') }}
                  <i class="icon icon--edit-white-size14"></i>
                </button>
              </div>
            </div>
          </template>
        </div>
        <div v-if="showBottomFade" class="log-content__item__fade" />
        <ui-tooltip
          v-if="hoveredTooltipItem && hoverIdx !== null && !isSelected(hoveredTooltipItem) && tooltipStyle"
          class="log-container__tooltip"
          :class="{ complete: getDisplayStatus(hoveredTooltipItem) === 'complete' }"
          :style="tooltipStyle"
          :arrow-center="getDisplayStatus(hoveredTooltipItem) !== 'complete'"
          small>
          <span v-html="getTooltipText(hoveredTooltipItem)"></span>
        </ui-tooltip>
      </div>
      <div class="discoveries-container">
        <div class="discoveries__title">
          {{ $t('reporting.timeMachine.discoveries.title') }}
        </div>
        <template v-if="selectedId === null">
          <div class="discoveries__placeholder">
            <p v-html="$t('reporting.timeMachine.discoveries.placeholder')"></p>
          </div>
        </template>
        <template v-else>
          <div class="discoveries__body">
            <div class="discoveries__section">
              <div class="discoveries__row" :class="$i18n.locale">
                <span class="discoveries__row__label">{{ $t('reporting.timeMachine.discoveries.segments') }}</span>
                <span class="discoveries__row__value">{{ discoveries.segments }}</span>
              </div>
              <div class="discoveries__row" :class="$i18n.locale">
                <span class="discoveries__row__label">{{ $t('reporting.timeMachine.discoveries.referrers') }}</span>
                <span class="discoveries__row__value">{{ discoveries.referrers }}</span>
              </div>
            </div>
            <div class="discoveries__section">
              <div class="discoveries__row" :class="$i18n.locale">
                <span class="discoveries__row__label">{{ $t('reporting.timeMachine.discoveries.averageAccess') }}</span>
                <span class="discoveries__row__value">{{ discoveries.averageAccess }}</span>
              </div>
              <div class="discoveries__row" :class="$i18n.locale">
                <span class="discoveries__row__label">{{ $t('reporting.timeMachine.discoveries.maximumAccess') }}</span>
                <span class="discoveries__row__value">{{ discoveries.maximumAccess }}</span>
              </div>
            </div>
            <div class="discoveries__section">
              <div class="discoveries__row" :class="$i18n.locale">
                <span class="discoveries__row__label">{{ $t('reporting.timeMachine.discoveries.eventsPerSecond') }}</span>
                <span class="discoveries__row__value">{{ discoveries.eventsPerSecond }}</span>
              </div>
              <div class="discoveries__row" :class="$i18n.locale">
                <span class="discoveries__row__label">{{ $t('reporting.timeMachine.discoveries.mostEvents') }}</span>
                <span class="discoveries__row__value">{{ discoveries.mostEvents }}</span>
              </div>
            </div>
            <div v-if="eventAreas.length" class="discoveries__event-focused">
              <div class="discoveries__event-focused__title">
                {{ $t('reporting.timeMachine.discoveries.eventFocused') }}
                <span class="discoveries__event-focused__note">({{ $t('reporting.timeMachine.discoveries.eventFocusedNote') }})</span>
              </div>
              <div class="discoveries__event-focused__labels">
                <span v-for="(label, idx) in 4" :key="idx" :class="$i18n.locale">
                  {{ $t(`reporting.timeMachine.discoveries.eventLabels[${idx}]`) }}
                </span>
              </div>
              <div class="discoveries__event-focused__list">
                <div v-for="(item, idx) in eventAreas" :key="idx"
                     class="discoveries__event-focused__item">
                  <span class="rank" :class="`rank--${idx + 1}`">{{ idx + 1 }}</span>
                  <span class="area">
                    <crop-image
                      v-if="item.coords && imageData"
                      :url="imageData.url"
                      :image-width="imageData.width"
                      :image-height="imageData.height"
                      :width="item.coords.width"
                      :height="item.coords.height"
                      :offset="{ x: item.coords.left, y: item.coords.top }"
                    />
                    <span v-else class="area__text">{{ item.name }}</span>
                  </span>
                  <span class="action">
                    <span v-if="item.typeLabel" class="action__type">{{ item.typeLabel }}</span>
                    <span class="action__event" :class="{ single: !item.typeLabel }">
                      <template v-if="item.typeLabel">/ </template>{{ item.actionLabel }}
                    </span>
                  </span>
                  <span class="rate">{{ item.rate }}%</span>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
    <TimeMachineReservationModal
      v-if="showReservationModal"
      :edit-item="editItem"
      :reservation-draft="reservationDraft"
      :timezoneName="timezoneName"
      :timezone="timezone"
      :needs-credit="needsCredit"
      @close="showReservationModal = false; editItem = null; reservationDraft = null"
      @confirm="onReservationConfirm"
    />
  </div>
</template>

<script>
import { format } from 'date-fns'
import { mapState } from 'vuex'

import { dateToLocalTimezone, utcToLocalTimezone, isAiDisabledAccount } from '@/libs/utils'
import { getTimeMachineReportExists } from '@/api/hub'

import TimeMachineReservationModal from './TimeMachineReservationModal'
import UiTooltip from '@/components/ui/UiTooltip'
import CropImage from '@/components/ui/CropImage'

export default {
  name: 'ReportTimeMachine',
  components: {
    TimeMachineReservationModal,
    UiTooltip,
    CropImage
  },
  props: {
    aid: {
      type: String,
      required: true
    },
    timeMachineData: {
      type: Array,
      default: () => []
    },
    topEventData: {
      type: Object,
      default: null
    },
    timezoneName: {
      type: String,
      default: ''
    },
    timezone: {
      type: String,
      default: ''
    },
    imageData: {
      type: Object,
      default: null
    },
    replayData: {
      type: Object,
      default: null
    },
    segment: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      showReservationModal: false,
      needsCredit: false,
      pendingData: null,
      reservationDraft: null,
      isHover: false,
      hoverIdx: null,
      hoveredTooltipItem: null,
      tooltipStyle: null,
      showBottomFade: false,
      editItem: null,
      selectedId: null,
      now: Date.now(),
      nowTimer: null,
      labels: ['control', 'start', 'end', 'duration', 'report']
    }
  },
  mounted () {
    this.nowTimer = setInterval(() => {
      this.now = Date.now()
    }, 60000)

    this.$nextTick(() => {
      this.syncScrollGuide()
    })
  },
  beforeDestroy () {
    if (this.nowTimer) {
      clearInterval(this.nowTimer)
    }
  },
  computed: {
    ...mapState([
      'applyData',
      'userData'
    ]),
    isData () {
      return this.timeMachineData.length
    },
    eventAreas () {
      if (!this.topEventData || !Array.isArray(this.topEventData.items)) return []
      const validItems = this.topEventData.items
        .filter(item => item.r && item.r.length >= 6 && Number.isFinite(Number(item.ratio)))
        .sort((a, b) => Number(b.ratio) - Number(a.ratio))
        .slice(0, 10)

      return validItems
        .map(item => ({
          name: item.dom,
          rate: Number(Number(item.ratio).toFixed(1)),
          event: item.event,
          coords: { top: item.r[0], left: item.r[3], width: item.r[4], height: item.r[5] },
          typeLabel: this.getDomTypeLabel(item.r[6]),
          actionLabel: this.getActionLabel(item.eventTypes)
        }))
    },
    segmentSummary () {
      const seg = this.segment || {}
      const deviceKey = seg.device || 'desktop'
      const device = this.$t(`reporting.timeMachine.discoveries.device.${deviceKey}`)
      let userTypeKey = 'all'
      if (seg.isNew && !seg.isReturn) userTypeKey = 'new'
      else if (!seg.isNew && seg.isReturn) userTypeKey = 'return'
      const userType = this.$t(`reporting.timeMachine.discoveries.userType.${userTypeKey}`)
      return {
        segments: `${device}, ${userType}`,
        referrers: !seg.referrer || seg.referrer === 'all'
          ? this.$t('reporting.timeMachine.discoveries.referrerAll')
          : seg.referrer
      }
    },
    replayStatistics () {
      const replay = this.replayData
      if (!replay || !Array.isArray(replay.frames) || !replay.frames.length) {
        return { averageAccess: '-', maximumAccess: '-', eventsPerSecond: '-', mostEvents: '-' }
      }

      const frames = replay.frames
      const totalEventCount = replay.totalEventCount || 0
      const activePvTotal = frames.reduce((sum, f) => sum + (f.activePv || 0), 0)
      const hasPv = activePvTotal > 0
      const hasEvents = totalEventCount > 0

      const avgPv = hasPv
        ? (frames.reduce((sum, f) => sum + (f.activePv || 0), 0) / frames.length).toFixed(3)
        : null
      const averageAccess = hasPv
        ? this.$t('reporting.timeMachine.discoveries.pvWithValue', { value: avgPv })
        : '-'

      const maxPvFrame = this.findMaxFrame(frames, 'activePv')
      const maxPvTime = this.formatFrameTime(maxPvFrame.timestamp)
      const maxPv = (maxPvFrame.activePv || 0).toLocaleString()
      const maximumAccess = hasPv
        ? this.$t('reporting.timeMachine.discoveries.pvWithTime', { value: maxPv, time: maxPvTime })
        : this.$t('reporting.timeMachine.discoveries.pvWithValue', { value: maxPv })

      const selectedItem = this.timeMachineData.find(item => item._id === this.selectedId)
      const durationSec = selectedItem ? selectedItem.duration * 60 : frames.length
      const eventsPerSec = hasEvents ? (totalEventCount / durationSec).toFixed(3) : null
      const eventsPerSecond = hasEvents
        ? this.$t('reporting.timeMachine.discoveries.countPerSecond', { count: eventsPerSec })
        : '-'

      const eventKey = frames[0].totalEventCount !== undefined
        ? 'totalEventCount'
        : (frames[0].eventCount !== undefined ? 'eventCount' : 'totalPv')
      const maxEventFrame = this.findMaxFrame(frames, eventKey)
      const maxEventTime = this.formatFrameTime(maxEventFrame.timestamp)
      const maxEvents = (maxEventFrame[eventKey] || 0).toLocaleString()
      const mostEvents = hasEvents
        ? this.$t('reporting.timeMachine.discoveries.countWithTime', { count: maxEvents, time: maxEventTime })
        : this.$t('reporting.timeMachine.discoveries.countWithValue', { count: maxEvents })

      return { averageAccess, maximumAccess, eventsPerSecond, mostEvents }
    },
    discoveries () {
      return { ...this.segmentSummary, ...this.replayStatistics }
    },
    isShared () {
      return this.userData.userId !== this.applyData.userId
    },
    isAiDisabled () {
      return isAiDisabledAccount(this.userData)
    }
  },
  watch: {
    timeMachineData () {
      this.$nextTick(() => {
        this.syncScrollGuide()
      })
    }
  },
  methods: {
    getActionLabel (eventTypes) {
      if (!Array.isArray(eventTypes) || eventTypes.length === 0) return ''
      const sorted = [...eventTypes].sort((a, b) => b.event - a.event)
      const topType = sorted[0].type
      const typeName = topType.charAt(0).toUpperCase() + topType.slice(1)
      if (sorted.length === 1) return typeName
      return this.$t('reporting.timeMachine.discoveries.eventTypeMore', {
        type: typeName,
        n: sorted.length - 1
      })
    },
    getDomTypeLabel (type) {
      if (!type) return ''
      const normalized = String(type).toLowerCase()
      if (normalized === 'a') return 'Link'
      return normalized.charAt(0).toUpperCase() + normalized.slice(1)
    },
    onContainerLeave () {
      this.isHover = false
      this.clearTooltip()
    },
    onTooltipEnter (item, idx, event) {
      this.hoverIdx = idx
      this.hoveredTooltipItem = item
      this.tooltipStyle = this.getTooltipOverlayStyle(item, event && event.currentTarget)
    },
    clearTooltip () {
      this.hoverIdx = null
      this.hoveredTooltipItem = null
      this.tooltipStyle = null
    },
    onLogBodyScroll () {
      this.clearTooltip()
      this.syncScrollGuide()
    },
    syncScrollGuide () {
      const body = this.$refs.logBody
      if (!body) {
        this.showBottomFade = false
        return
      }

      const hasOverflow = body.scrollHeight > body.clientHeight + 1
      const isAtBottom = body.scrollTop + body.clientHeight >= body.scrollHeight - 1
      this.showBottomFade = hasOverflow && !isAtBottom
    },
    getTooltipOverlayStyle (item, target) {
      const container = this.$refs.logContainer
      if (!container || !target || typeof container.getBoundingClientRect !== 'function' || typeof target.getBoundingClientRect !== 'function') {
        return null
      }

      const containerRect = container.getBoundingClientRect()
      const targetRect = target.getBoundingClientRect()
      const isComplete = this.getDisplayStatus(item) === 'complete'
      const left = isComplete
        ? targetRect.left - containerRect.left
        : targetRect.left - containerRect.left + (targetRect.width / 2)

      return {
        top: `${targetRect.top - containerRect.top - 8}px`,
        left: `${left}px`,
        transform: isComplete ? 'translateY(-100%)' : 'translate(-50%, -100%)'
      }
    },
    onAddReservation () {
      this.needsCredit = false
      this.editItem = null
      this.reservationDraft = null
      this.showReservationModal = true
    },
    reopenReservationModal (data) {
      this.needsCredit = false
      this.editItem = null
      this.reservationDraft = data
      this.showReservationModal = true
    },
    openModalWithCredit () {
      if (this.pendingData) {
        this.$emit('add-reservation-with-credit', this.pendingData)
        this.pendingData = null
      }
    },
    parseUTC (dateStr) {
      if (!dateStr) return null
      const str = dateStr.endsWith('Z') ? dateStr : dateStr + 'Z'
      const d = new Date(str)
      return isNaN(d.getTime()) ? null : d
    },
    toTzDate (dateStr) {
      const d = this.parseUTC(dateStr)
      if (!d) return null

      return this.timezoneName ? utcToLocalTimezone(d, this.timezoneName) : d
    },
    formatTzDate (dateStr) {
      const d = this.toTzDate(dateStr)
      return d ? format(d, 'YYYY-MM-DD') : ''
    },
    formatTzTime (dateStr) {
      const d = this.toTzDate(dateStr)
      return d ? format(d, 'HH:mm') : ''
    },
    findMaxFrame (frames, key) {
      let max = frames[0]
      frames.forEach(f => {
        if ((f[key] || 0) > (max[key] || 0)) max = f
      })
      return max
    },
    formatFrameTime (timestamp) {
      if (!timestamp) return ''
      const d = typeof timestamp === 'string' ? this.parseUTC(timestamp) : new Date(timestamp)
      if (!d || isNaN(d.getTime())) return ''
      const tzDate = this.timezoneName ? utcToLocalTimezone(d, this.timezoneName) : d
      return format(tzDate, 'HH:mm:ss')
    },
    isSelected (item) {
      return item._id === this.selectedId
    },
    getDisplayStatus (item) {
      if (item.status === 'complete') return 'complete'
      if (item.status === 'waiting') return 'waiting'
      return 'reservation'
    },
    getStatusIcon (item) {
      const status = this.getIconStatus(item)
      if (status === 'reservation') return 'icon--time-machine-reservation'
      if (status === 'start') return 'icon--time-machine-start'
      return this.isSelected(item) ? 'icon--time-machine-replay' : 'icon--time-machine-replay-default'
    },
    getIconStatus (item) {
      if (item.status === 'start' || item.status === 'waiting') return 'start'
      if (item.status === 'complete') return 'complete'
      if (item.status === 'reservation') return 'reservation'
      return 'reservation'
    },
    getRowStyle (item) {
      return null
    },
    getTooltipText (item) {
      const status = this.getDisplayStatus(item)
      const key = `reporting.timeMachine.tooltip.${status}`
      return this.$t(key).replace(/\n/g, '<br>')
    },
    onEditReservation (item) {
      this.editItem = item
      this.reservationDraft = null
      this.showReservationModal = true
    },
    onReservationConfirm (data) {
      this.showReservationModal = false
      this.reservationDraft = null
      if (this.editItem) {
        this.$emit('modify-reservation', { ...data, id: this.editItem._id })
        this.editItem = null
        return
      }
      const localDate = new Date(
        data.startDate.getFullYear(),
        data.startDate.getMonth(),
        data.startDate.getDate(),
        Number(data.hour),
        Number(data.minute)
      )
      const utcStart = dateToLocalTimezone(localDate, this.applyData.timezoneName)

      getTimeMachineReportExists({ aid: this.aid, startDate: utcStart.toISOString() }).then(res => {
        if (res && res.success && res.result && res.result.exists) {
          this.pendingData = data
          this.$emit('duplicate-date')
        } else {
          this.$emit('add-reservation', data)
        }
      })
    },
    onSelectRow (item) {
      if (this.getDisplayStatus(item) === 'complete') {
        if (this.selectedId === item._id) {
          this.selectedId = null
          this.$emit('select-report', null)
        } else {
          this.selectedId = item._id
          this.$emit('select-report', item)
        }
      }
    },
    isReportDisabled (item) {
      if (this.isAiDisabled) return true
      return this.isShared || this.getDisplayStatus(item) !== 'complete'
    },
    onClickReport (item) {
      if (this.isReportDisabled(item)) return
      this.$emit('create-report', item)
    }
  }
}
</script>

<style scoped lang="scss">
.reporting__time-machine {
  width: 100%;

  div {
    box-sizing: border-box;
  }

  .title {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #444;
    font-size: 22px;
    font-weight: 500;
    line-height: 22px;

    .icon--beta {
      margin-left: 2px;
    }
  }

  .content {
    width: 100%;
    margin-top: 20px;

    .log-container {
      min-height: 370px;
      max-height: 473px;
      position: relative;
      border-radius: 14px;
      background: #FFF;
      box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08), 0 23px 15px -12px rgba(0, 0, 0, 0.08);
      padding: 20px 0 40px 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 58px;

      &.isData {
        gap: 0;

        .log-header {
          margin-bottom: 34px;
        }

        .log-content {
          justify-content: flex-start;
        }
      }

      .log-header {
        padding: 0 25px;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;

        &__title {
          color: #2F2F2F;
          font-size: 15px;
          font-weight: 500;
          line-height: 15px;
        }

        &__actions {
          &__button {
            display: flex;
            height: 32px;
            min-width: 80px;
            padding: 0 12px 0 10px;
            align-items: center;
            gap: 4px;
            border-radius: 999px;
            background: #EC0047;
            position: relative;

            &:hover {
              & > .text {
                z-index: 1;
              }
              &:after {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background-color: rgba(0, 0, 0, 0.16);
                border-radius: 999px;
              }
            }

            .text {
              min-width: 41px;
              display: inline-block;
              text-align: center;
              color: #FFF;
              font-size: 13px;
              font-weight: 600;
              line-height: 100%;
            }
          }
        }
      }

      .log-content {
        width: 100%;
        max-height: 370px;
        overflow-y: auto;
        scrollbar-gutter: stable;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 24px;
        padding: 0 25px;

        &__icon {
          .wait-icon {
            width: 211px;
            height: 126px;
          }
        }

        &__text {
          color: #2F2F2F;
          text-align: center;
          font-size: 13px;
          font-weight: 400;
          line-height: 19px;
        }

        &__item {
          width: 100%;
          display: flex;
          flex-direction: column;
          position: relative;

          &__label {
            width: calc(100% - 50px);
            padding-bottom: 10px;
            box-sizing: border-box;
            background: #fff;
            display: flex;
            align-items: center;
            justify-content: space-between;
            border-bottom: 1px solid #bbb;

            &__text {
              color: #444;
              font-size: 12px;
              font-weight: 400;
              line-height: 100%;
              text-align: center;
              position: relative;

              &.control {
                width: 36px;
                flex-shrink: 0;
                padding-left: 3px;

                &.ja {
                  padding-left: 3px;
                }
              }

              &.start, &.end {
                width: 72px;
                flex-shrink: 0;
              }

              &.end {
                margin-left: -6px;
              }

              &.duration {
                width: 50px;
                flex-shrink: 0;
                &:not(.ja) {
                  margin-left: -3px;
                }
              }

              &.report {
                min-width: 38px;
                flex-shrink: 0;
                text-align: right;
                margin-right: 15px;

                &.ja  {
                  margin-right: 10px;
                }
              }
            }
          }

          &__body {
            width: 100%;
          }

          &__fade {
            position: absolute;
            left: 20px;
            right: 20px;
            bottom: 39px;
            height: 12px;
            background: linear-gradient(180deg, rgba(255, 255, 255, 0.00) 0%, #FFF 100%);
            pointer-events: none;
          }

          &__row {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 9px;
            padding: 14px 5px;
            position: relative;
            max-height: 56px;
            cursor: pointer;
            transition: background 0.3s ease;

            &:not(:last-child) {
              border-bottom: 1px dashed #d7d7d7;
            }

            &.selected,
            &:has(+ .selected) {
              border-bottom-color: transparent;
            }

            &.selected {
              z-index: 1;

              &::before {
                content: '';
                position: absolute;
                inset: 0;
                box-sizing: border-box;
                border: 2px solid #EC0047;
                border-radius: 6px;
                box-shadow: 0 10px 12px -2px rgba(0, 0, 0, 0.25);
                pointer-events: none;
              }

              > * {
                position: relative;
                z-index: 1;
              }

              .report {
                cursor: default;
              }
            }

            &.waiting {
              &::after {
                content: '';
                position: absolute;
                bottom: 0;
                left: 50%;
                transform: translateX(-50%);
                width: calc(100% - 40px);
                height: 2px;
              }

              &:hover .log-content__item__row__edit-btn {
                display: flex;
              }
            }

            &:not(.selected):hover {
              background: rgba(0, 0, 0, 0.06);
            }

            &__edit-btn {
              display: none;
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              align-items: center;
              gap: 4px;
              height: 36px;
              padding: 9.5px 10px 9.5px 12px;
              border-radius: 999px;
              background: #FFF;
              box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.2);
              color: #444;
              text-align: center;
              font-size: 13px;
              font-weight: 600;
              line-height: 100%;
              white-space: nowrap;
              cursor: pointer;
              z-index: 2;

              .icon {
                filter: brightness(0) saturate(100%) invert(30%);
              }
            }

            &__cell {
              display: flex;
              align-items: center;

              > span {
                color: #444;
                font-size: 12px;
                font-weight: 400;
                line-height: 100%;
              }

              &.control {
                width: 36px;
                flex-shrink: 0;
                justify-content: center;
                position: relative;

                .start-icon {
                  width: 28px;
                  height: 28px;
                }

                .bubble {
                  bottom: calc(100% + 8px);
                  left: 50%;
                  transform: translateX(-50%);
                  padding: 9px 10px;

                  /deep/ .text {
                    color: #444;
                    text-align: center;
                    font-size: 12px;
                    font-weight: 400;
                    line-height: 100%;
                  }
                }

                &.complete .bubble {
                  left: 0;
                  transform: none;
                  padding: 16px;
                }
              }

              &.date {
                width: 72px;
                flex-direction: column;
                gap: 4px;
              }

              &.duration {
                width: 50px;
                justify-content: center;
                color: #444;
                font-size: 12px;
                font-weight: 400;
                line-height: 100%;
                margin-right: 6px;
              }

              &.report {
                justify-content: flex-end;
                margin-right: 11px;
                padding: 7px;
              }
            }
          }
        }
      }

      &__tooltip {
        pointer-events: none;
        padding: 9px 10px;

        /deep/ .text {
          color: #444;
          text-align: center;
          font-size: 12px;
          font-weight: 400;
          line-height: 100%;
        }

        &.complete {
          padding: 16px;

          /deep/ .text {
            text-align: left;
            line-height: 150%;
            font-size: 13px;
          }
        }
      }
    }

    .discoveries-container {
      border-radius: 14px;
      background: #FFF;
      box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08), 0 23px 15px -12px rgba(0, 0, 0, 0.08);
      margin-top: 30px;
      padding: 25px;
      display: flex;
      flex-direction: column;
      min-height: 340px;

      .discoveries__placeholder {
        display: flex;
        justify-content: center;
        flex: 1;
        margin-top: 97px;

        p {
          color: #2F2F2F;
          text-align: center;
          font-size: 13px;
          font-style: normal;
          font-weight: 400;
          line-height: 19px; /* 146.154% */
        }
      }

      .discoveries__title {
        color: #2F2F2F;
        font-size: 15px;
        font-weight: 600;
        line-height: 15px;
      }

      .discoveries__body {
        margin-top: 30px;
      }

      .discoveries__section {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
        gap: 12px 60px;
        padding: 20px 0;
        border-bottom: 1px dashed #d7d7d7;

        &:first-child {
          padding-top: 0;
        }

        &:nth-last-child(2) {
          padding-bottom: 0;
          border-bottom: none;
        }
      }

      .discoveries__row {
        display: flex;
        align-items: center;
        gap: 10px;

        &__label {
          width: 90px;
          flex-shrink: 0;
          color: #666;
          font-size: 13px;
          font-weight: 500;
          line-height: 100%;
        }

        &__value {
          color: #444;
          font-size: 13px;
          font-weight: 600;
          line-height: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          min-width: 0;
        }

        &.en .discoveries__row__label {
          width: 120px;
        }

        &.ja .discoveries__row__label {
          width: 100px;
        }
      }

      .discoveries__event-focused {
        padding-top: 60px;

        &__title {
          color: #2F2F2F;
          font-size: 13px;
          font-weight: 500;
          line-height: 13px;
          margin-bottom: 20px;
        }

        &__note {
          font-size: 12px;
          color: #797979;
          font-weight: 500;
          margin-left: 2px;
        }

        &__labels {
          display: grid;
          grid-template-columns: 44px minmax(0, 340px) 120px 58px;
          column-gap: clamp(8px, 3%, 24px);
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid #bbb;
          padding: 0 clamp(8px, 3%, 18px) 8px;

          span {
            color: #444;
            font-size: 12px;
            font-weight: 400;
            line-height: 100%;
            text-align: center;
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;

            &:first-child {
              text-align: left;
            }

            &:nth-child(2) {
              margin-left: -4px;
            }

            &:nth-child(3) {
              text-align: left;
              padding-left: 6px;

              &.ko {
                padding-left: 18px;
              }
            }

            &:last-child {
              text-align: right;
            }
          }
        }

        &__item {
          display: grid;
          grid-template-columns: 44px minmax(0, 340px) 120px 58px;
          column-gap: clamp(8px, 3%, 24px);
          justify-content: space-between;
          align-items: center;
          padding: 14px clamp(8px, 3%, 18px);
          transition: background 0.15s;

          &:not(:last-child) {
            border-bottom: 1px dashed #d7d7d7;
          }

          &:hover {
            background: rgba(0, 0, 0, 0.06);
          }

          .rank {
            width: 36px;
            height: 36px;
            border-radius: 50%;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            box-sizing: border-box;
            color: #EC0047;
            font-size: 14px;
            font-weight: 600;
            line-height: 100%;
            fill: #FFF;
            border: 1px solid #C1C1C1;
            justify-self: start;

            &--1 {
              background: #FEE9E6;
              border: 1px solid #EC0047;
            }
            &--2 {
              background: #E0F6FA;
              border: 1px solid #0E90A5;
              color: #0E90A5;
            }
            &--3 {
              background: #FFF6C6;
              border: 1px solid #DD934E;
              color: #BB793A;
            }
          }

          .area {
            min-width: 0;
            display: flex;
            justify-content: center;
            align-items: center;

            img {
              max-height: 38px;
              border-radius: 50%;
              object-fit: cover;
            }

            &__text {
              color: #444;
              font-size: 12px;
              font-weight: 400;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
          }

          .action {
            display: flex;
            align-items: center;
            flex-direction: column;
            justify-content: center;
            transform: translateX(-16px);
            color: #444;
            font-size: 12px;
            font-weight: 400;
            min-height: 36px;
            line-height: 16px;
            min-width: 0;
            overflow: hidden;
            text-align: center;

            &__type,
            &__event {
              display: block;
              max-width: 100%;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }

            &__event.single {
              line-height: 100%;
            }
          }

          .rate {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            min-height: 36px;
            min-width: 0;
            text-align: right;
            color: #2F2F2F;
            font-size: 13px;
            font-weight: 600;
            white-space: nowrap;
          }
        }

      }
    }

    @media (min-width: 1840px) {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 30px;
      align-items: stretch;

      .log-container {
        min-height: calc(100vh - 200px);
        max-height: calc(100vh - 200px);
        overflow: hidden;

        .log-content {
          max-height: 100%;
        }
      }

      .discoveries-container {
        margin-top: 0;
        max-height: calc(100vh - 200px);
        overflow: hidden;

        .discoveries__body {
          flex: 1;
          min-height: 0;
          display: flex;
          flex-direction: column;
        }

        .discoveries__section {
          flex-shrink: 0;
        }

        .discoveries__event-focused {
          flex: 1;
          display: flex;
          flex-direction: column;
          min-height: 0;
        }

        .discoveries__event-focused__title,
        .discoveries__event-focused__labels {
          flex-shrink: 0;
        }

        .discoveries__event-focused__list {
          flex: 1;
          min-height: 0;
          max-height: unset;
          overflow-y: auto;
        }
      }
    }
  }
}
</style>
